import { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, NavLink, useParams } from 'react-router-dom';
import { api } from '../../../../services/api';
import { useWorkspace } from '../../WorkspaceContext';
import { useWorkspaceCurrency } from '../../hooks/useWorkspaceCurrency';
import {
  frontDeskSubtabs,
  WORKSPACE_ROOT,
} from '../../navigation';
import {
  addMonths,
  formatCurrency,
  formatDateKey,
  formatDateKeyFromMs,
  formatDisplayDate,
  rangeBoundsMs,
} from '../../utils/datetime';
import FrontDeskStats from './components/FrontDeskStats';
import NextUpCard from './components/NextUpCard';
import TodayAppointments from './components/TodayAppointments';
import WaitingQueue from './components/WaitingQueue';
import CheckoutList from './components/CheckoutList';
import TillSummary from './components/TillSummary';
import DayCloseSummary from './components/DayCloseSummary';
import FrontDeskMenu from './components/FrontDeskMenu';

const FrontDesk = () => {
  const { section: sectionParam } = useParams();
  const section = frontDeskSubtabs.find((item) => item.id === sectionParam)?.id || null;
  const { business, activeLocation } = useWorkspace();
  const { currency } = useWorkspaceCurrency();
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [updatingId, setUpdatingId] = useState(null);

  const todayKey = formatDateKey();
  const horizonKey = addMonths(todayKey, 3);

  const load = useCallback(
    async ({ soft = false } = {}) => {
      if (!activeLocation) {
        setLoading(false);
        return;
      }

      if (soft) setRefreshing(true);
      else setLoading(true);

      const range = rangeBoundsMs(todayKey, horizonKey);
      const result = await api.getWorkspaceAppointments({
        from: range.from,
        to: range.to,
        locationId: activeLocation.id,
      });

      if (result.error) {
        setError(result.error);
        setAppointments([]);
      } else {
        setError('');
        setAppointments(Array.isArray(result.data) ? result.data : []);
      }

      setLoading(false);
      setRefreshing(false);
    },
    [activeLocation, todayKey, horizonKey]
  );

  useEffect(() => {
    load();
  }, [load]);

  // Keep the desk reasonably fresh while the page is open.
  useEffect(() => {
    const timer = setInterval(() => load({ soft: true }), 60_000);
    return () => clearInterval(timer);
  }, [load]);

  const todayAppointments = useMemo(
    () => appointments.filter((appt) => formatDateKeyFromMs(appt.startAt) === todayKey),
    [appointments, todayKey]
  );

  const activeAppointments = useMemo(
    () => todayAppointments.filter((appt) => appt.status !== 'cancelled'),
    [todayAppointments]
  );

  const upcoming = useMemo(() => {
    const now = Date.now();
    return activeAppointments
      .filter((appt) => appt.status === 'confirmed' && appt.startAt >= now - 30 * 60 * 1000)
      .sort((a, b) => a.startAt - b.startAt);
  }, [activeAppointments]);

  const nextUp = useMemo(() => {
    const now = Date.now();
    return (
      appointments
        .filter((appt) => appt.status === 'confirmed' && appt.startAt >= now - 30 * 60 * 1000)
        .sort((a, b) => a.startAt - b.startAt)[0] || null
    );
  }, [appointments]);

  const arrived = activeAppointments.filter((appt) => appt.status === 'arrived');
  const completed = activeAppointments.filter((appt) => appt.status === 'completed');
  const revenue = completed.reduce(
    (sum, appt) => sum + (Number(appt.price) || Number(appt.service?.price) || 0),
    0
  );

  const stats = {
    upcoming: upcoming.length,
    arrived: arrived.length,
    completed: completed.length,
    revenueLabel: formatCurrency(revenue, currency),
  };

  const updateStatus = async (appointmentId, status) => {
    setUpdatingId(appointmentId);
    setError('');
    const result = await api.updateWorkspaceAppointment(appointmentId, { status });
    setUpdatingId(null);
    if (result.error) {
      setError(result.error);
      return;
    }
    await load({ soft: true });
  };

  const completePayment = async (appointmentId, payload) => {
    setUpdatingId(appointmentId);
    setError('');
    const result = await api.updateWorkspaceAppointment(appointmentId, {
      status: 'completed',
      paymentMethod: payload.paymentMethod,
      amountPaid: payload.amountPaid,
      amountDue: payload.amountDue ?? 0,
      paidAt: Date.now(),
    });
    setUpdatingId(null);
    if (result.error) {
      setError(result.error);
      return;
    }
    await load({ soft: true });
  };

  if (!section) {
    return <Navigate to={`${WORKSPACE_ROOT}/front-desk/today`} replace />;
  }

  return (
    <div className="mx-auto w-full max-w-7xl px-4 py-6 sm:px-6 sm:py-10 lg:px-8">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="text-xs uppercase tracking-[0.22em] text-saffron-600">Front desk</p>
          <h1 className="mt-2 font-display text-3xl font-light tracking-tight text-ink-900 sm:text-4xl">
            {formatDisplayDate(todayKey)}
          </h1>
        </div>
        <FrontDeskMenu
          refreshing={refreshing || loading}
          onRefresh={() => load({ soft: true })}
        />
      </div>

      {error && (
        <div className="mt-6 border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800">
          {error}
        </div>
      )}

      <nav aria-label="Front desk sections" className="-mx-4 mt-6 border-b border-ink-900/10 px-4 sm:mx-0 sm:mt-8 sm:px-0">
        <div className="scrollbar-hide -mb-px flex gap-0.5 overflow-x-auto overscroll-x-contain">
          {frontDeskSubtabs.map((item) => (
            <NavLink
              key={item.id}
              to={`${WORKSPACE_ROOT}/front-desk/${item.path}`}
              className={({ isActive }) =>
                `flex-none whitespace-nowrap border-b-2 px-3 py-3 text-sm font-medium transition-colors sm:px-4 ${
                  isActive
                    ? 'border-saffron-500 text-ink-900'
                    : 'border-transparent text-ink-600/70 hover:border-ink-900/20 hover:text-ink-900'
                }`
              }
            >
              <span className="sm:hidden">{item.shortLabel}</span>
              <span className="hidden sm:inline">{item.label}</span>
            </NavLink>
          ))}
        </div>
      </nav>

      {loading ? (
        <div className="flex justify-center py-20">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-ink-900/20 border-t-saffron-500" />
        </div>
      ) : (
        <div className="mt-6 space-y-6 sm:mt-8">
          {(section === 'today' || section === 'queue' || section === 'checkout') && (
            <FrontDeskStats stats={stats} />
          )}

          {section === 'today' && (
            <>
              <NextUpCard
                appointment={nextUp}
                currency={currency}
                onArrive={(id) => updateStatus(id, 'arrived')}
              />
              <TodayAppointments
                appointments={activeAppointments}
                currency={currency}
                onStatusChange={(id, status) => {
                  if (updatingId) return;
                  updateStatus(id, status);
                }}
              />
            </>
          )}

          {section === 'queue' && (
            <WaitingQueue
              waiting={arrived}
              currency={currency}
              onStatusChange={(id, status) => {
                if (updatingId) return;
                updateStatus(id, status);
              }}
            />
          )}

          {section === 'checkout' && (
            <CheckoutList
              ready={arrived}
              completed={completed}
              currency={currency}
              onCompletePayment={(id, payload) => {
                if (updatingId) return;
                return completePayment(id, payload);
              }}
            />
          )}

          {section === 'till' && <TillSummary completed={completed} currency={currency} />}

          {section === 'close' && (
            <DayCloseSummary appointments={todayAppointments} currency={currency} />
          )}
        </div>
      )}
    </div>
  );
};

export default FrontDesk;
